import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { parseQueryParamToList } from "@/helpers/query-params";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useQueryParams } from "@/hooks/useQueryParam";
import { usePlusButtonQueries } from "@/queries/plus-button/queryOptions";
import type { ResidentTicketTabs } from "@/queries/tickets/constants";
import {
  RESIDENT_CLOSED_TICKET_TAB,
  RESIDENT_OPEN_TICKET_TAB,
  RESIDENT_RECENT_TICKET_TAB,
} from "@/queries/tickets/constants";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import type { ApiQueryParams } from "@/types/api-types";

import type { ResidentTicketsFilterValues } from "./components/ResidentTicketsFilterDrawer";
import { ResidentTicketListContent, type ResidentTicketListContentProps } from "./ResidentTicketListContent";

const TABS: ResidentTicketTabs[] = [RESIDENT_OPEN_TICKET_TAB, RESIDENT_CLOSED_TICKET_TAB, RESIDENT_RECENT_TICKET_TAB];
type RouteParams = {
  statusIds?: string;
  categoryIds?: string;
  reporterIds?: string;
  visibility?: string;
  tab?: string;
};

type AllResidentTicketsQueryParams = ApiQueryParams<"getTicketsV1">;

export function ResidentTicketListPage(): React.ReactNode {
  const [search, setSearch] = useState("");
  const debouncedQuery = useDebounceValue(search.toLowerCase().trim());
  const [queryParams, setQueryParams] = useQueryParams<RouteParams>();
  const activeTab =
    queryParams.tab && TABS.includes(queryParams.tab as any) ? (queryParams.tab as ResidentTicketTabs) : TABS[0];
  const currFilter: ResidentTicketsFilterValues = useMemo(() => {
    return {
      categoryIds: parseQueryParamToList(queryParams.categoryIds),
      reporterIds: parseQueryParamToList(queryParams.reporterIds),
      visibility: (queryParams.visibility || "both") as ResidentTicketsFilterValues["visibility"],
      statusIds: parseQueryParamToList(queryParams.statusIds),
    };
  }, [queryParams]);

  const sessionUser = useSessionUser();

  const handleUpdateFilter = (filter: ResidentTicketsFilterValues) => {
    setQueryParams({
      ...queryParams,
      categoryIds: filter.categoryIds ? filter.categoryIds.join(",") : undefined,
      reporterIds: filter.reporterIds ? filter.reporterIds.join(",") : undefined,
      visibility: filter.visibility !== "both" ? filter.visibility : undefined,
      statusIds: filter.statusIds ? filter.statusIds.join(",") : undefined,
    });
  };

  const handleChangeTab = (tab: ResidentTicketTabs) => {
    setQueryParams({
      ...queryParams,
      tab,
    });
  };

  const query = {
    Search: debouncedQuery,
    CategoryIds: currFilter.categoryIds,
    RepairRequestFilterType: currFilter.visibility,
    StatusIds: currFilter.statusIds,
    StatusType: activeTab,
  } satisfies AllResidentTicketsQueryParams;
  const amountActiveFilters = Object.entries(currFilter).filter(([key, value]) => {
    if (key === "visibility" && value === "both") {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return value != null;
  }).length;

  const ticketQueries = useTicketQueries();
  const {
    data: ticketData,
    hasNextPage: hasMoreTickets,
    isFetching: isFetchingTickets,
    isFetchingNextPage: isFetchingMoreTickets,
    fetchNextPage: loadMoreTickets,
    error: ticketsError,
  } = useInfiniteQuery(ticketQueries.listForResident(query));
  const plusButtonQueries = usePlusButtonQueries();
  const { data: plusButtonOptions, isPending: isPendingPlusButtonOptions } = useQuery(plusButtonQueries.list());

  const { data: tabsStats, error: tabsStatsError } = useQuery(
    // exclude tab and sortby to prevent refetching because they do not affect the amount of tickets
    ticketQueries.residentTabsStats({ ...query, StatusType: undefined }),
  );

  const { data: totalTicketsInTab, error: totalTicketsInTabError } = useQuery({
    ...ticketQueries.residentTabTotal(activeTab),
    enabled: amountActiveFilters > 0,
  });

  const {
    data: ticketFilterValues,
    isLoading: isLoadingTicketFilterValues,
    error: ticketFilterValuesError,
  } = useQuery(ticketQueries.residentFilterOptions());

  const totalTickets = ticketData?.pages[0].total;
  const tickets = useMemo(() => ticketData?.pages.flatMap((x) => x.items) ?? [], [ticketData]);

  if (ticketsError || totalTicketsInTabError || tabsStatsError || ticketFilterValuesError) {
    return <ErrorPage error={ticketsError} />;
  }

  if (isLoadingTicketFilterValues || isPendingPlusButtonOptions) {
    return <FullSizeLoader withPadding />;
  }

  const contentProps: ResidentTicketListContentProps = {
    tab: activeTab,
    onChangeTab: handleChangeTab,
    filter: currFilter,
    onUpdateFilter: handleUpdateFilter,
    search,
    onChangeSearch: setSearch,
    sessionUser,
    totalTickets,
    totalTicketsInTab: amountActiveFilters > 0 ? totalTicketsInTab?.tickets : totalTickets,
    tabsStats,
    tickets,
    isLoadingTickets: isFetchingTickets,
    isLoadingMoreTickets: isFetchingMoreTickets,
    hasMoreTickets,
    loadMoreTickets,
    filterOptions: ticketFilterValues ?? { statuses: [], categories: [], assignees: [] },
    plusButtonOptions: plusButtonOptions ?? [],
  };

  return <ResidentTicketListContent {...contentProps} />;
}
